/**
 * Web Crypto API encryption utilities for secure password storage.
 * Uses AES-GCM encryption with a derived key.
 */

const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const ITERATIONS = 100000;

/**
 * Get or create encryption key from localStorage.
 * In production, this should be derived from user authentication.
 */
async function getEncryptionKey(): Promise<CryptoKey> {
  const keyData = localStorage.getItem('_encryption_key');
  
  if (keyData) {
    const rawKey = Uint8Array.from(atob(keyData), c => c.charCodeAt(0));
    return crypto.subtle.importKey(
      'raw',
      rawKey,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // Generate new key
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  // Store key (in production, derive from user password)
  const exportedKey = await crypto.subtle.exportKey('raw', key);
  const keyArray = new Uint8Array(exportedKey);
  const keyString = btoa(String.fromCharCode.apply(null, Array.from(keyArray)));
  localStorage.setItem('_encryption_key', keyString);

  return key;
}

/**
 * Encrypt a string using Web Crypto API.
 */
export async function encryptString(plaintext: string): Promise<string> {
  try {
    const key = await getEncryptionKey();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
    const encoder = new TextEncoder();
    const data = encoder.encode(plaintext);

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(IV_LENGTH + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), IV_LENGTH);

    // Convert to base64
    return btoa(String.fromCharCode.apply(null, Array.from(combined)));
  } catch (error) {
    console.error('Encryption failed:', error);
    // Fallback to base64 encoding if crypto fails
    return btoa(plaintext);
  }
}

/**
 * Decrypt a string using Web Crypto API.
 */
export async function decryptString(ciphertext: string): Promise<string> {
  try {
    const key = await getEncryptionKey();
    
    // Convert from base64
    const combined = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));

    // Extract IV and encrypted data
    const iv = combined.slice(0, IV_LENGTH);
    const data = combined.slice(IV_LENGTH);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Decryption failed:', error);
    // Fallback to base64 decoding if crypto fails
    try {
      return atob(ciphertext);
    } catch {
      return ciphertext;
    }
  }
}

/**
 * Check if Web Crypto API is available.
 */
export function isCryptoSupported(): boolean {
  return typeof crypto !== 'undefined' && 
         typeof crypto.subtle !== 'undefined' &&
         typeof crypto.subtle.encrypt === 'function';
}

/**
 * Encrypt password before storing.
 */
export async function encryptPassword(password: string): Promise<string> {
  if (!isCryptoSupported()) {
    console.warn('Web Crypto API not available, using base64 encoding');
    return btoa(password);
  }
  return encryptString(password);
}

/**
 * Decrypt password after retrieving.
 */
export async function decryptPassword(encrypted: string): Promise<string> {
  if (!encrypted) return '';
  
  if (!isCryptoSupported()) {
    try {
      return atob(encrypted);
    } catch {
      return encrypted;
    }
  }
  
  return decryptString(encrypted);
}

